/**
 * Water Tank Calculator
 * Calculates total area (bottom + walls) for round and rectangular water tanks
 */

const WaterTankCalculator = {
  /**
   * Initialize calculator for a specific instance
   * @param {string} uniqueId - Unique identifier for this calculator instance
   */
  init(uniqueId) {
    const container = document.getElementById(`water-tank-calculator-${uniqueId}`);
    if (!container) return;

    // Inject step buttons (+/-) for all input groups (same as area-calculator)
    container.querySelectorAll('.water-tank-calculator__input-group').forEach((group) => {
      const input = group.querySelector('input[type="number"]');
      const unit = group.querySelector('.water-tank-calculator__unit');
      if (!input) return;

      const inputContainer = document.createElement('div');
      inputContainer.className = 'water-tank-calculator__input-container';

      const wrapper = document.createElement('div');
      wrapper.className = 'water-tank-calculator__input-wrapper';

      const step = parseFloat(input.step) || 0.01;
      const min = parseFloat(input.min) || 0.01;
      const precision = step < 0.01 ? 1000 : 100;

      const minusBtn = document.createElement('button');
      minusBtn.type = 'button';
      minusBtn.className = 'water-tank-calculator__step-btn';
      minusBtn.setAttribute('aria-label', 'Decrease');
      minusBtn.textContent = '−';
      minusBtn.addEventListener('click', () => {
        const val = parseFloat(input.value) || min;
        const next = Math.max(min, Math.round((val - step) * precision) / precision);
        input.value = next;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });

      const plusBtn = document.createElement('button');
      plusBtn.type = 'button';
      plusBtn.className = 'water-tank-calculator__step-btn';
      plusBtn.setAttribute('aria-label', 'Increase');
      plusBtn.textContent = '+';
      plusBtn.addEventListener('click', () => {
        const val = parseFloat(input.value) || min;
        const next = Math.round((val + step) * precision) / precision;
        input.value = next;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });

      if (unit) unit.remove();
      wrapper.appendChild(input);
      if (unit) wrapper.appendChild(unit);

      inputContainer.appendChild(minusBtn);
      inputContainer.appendChild(wrapper);
      inputContainer.appendChild(plusBtn);

      const label = group.querySelector('.water-tank-calculator__label');
      if (label && label.nextSibling) {
        while (label.nextSibling) label.nextSibling.remove();
      }
      group.appendChild(inputContainer);
    });

    // Round tank inputs
    const roundDiameterInput = container.querySelector('#round-diameter-' + uniqueId);
    const roundHeightInput = container.querySelector('#round-height-' + uniqueId);
    const roundResult = container.querySelector('#round-result-' + uniqueId);

    // Rectangular tank inputs
    const rectangularLengthInput = container.querySelector('#rectangular-length-' + uniqueId);
    const rectangularWidthInput = container.querySelector('#rectangular-width-' + uniqueId);
    const rectangularHeightInput = container.querySelector('#rectangular-height-' + uniqueId);
    const rectangularResult = container.querySelector('#rectangular-result-' + uniqueId);

    // Round tank event listeners
    if (roundDiameterInput) {
      roundDiameterInput.addEventListener('input', () => {
        this.calculateRoundTank(uniqueId);
      });
    }

    if (roundHeightInput) {
      roundHeightInput.addEventListener('input', () => {
        this.calculateRoundTank(uniqueId);
      });
    }

    // Rectangular tank event listeners
    if (rectangularLengthInput) {
      rectangularLengthInput.addEventListener('input', () => {
        this.calculateRectangularTank(uniqueId);
      });
    }

    if (rectangularWidthInput) {
      rectangularWidthInput.addEventListener('input', () => {
        this.calculateRectangularTank(uniqueId);
      });
    }

    if (rectangularHeightInput) {
      rectangularHeightInput.addEventListener('input', () => {
        this.calculateRectangularTank(uniqueId);
      });
    }

    // Calculate initial results if default values are set
    if (roundDiameterInput && roundHeightInput) {
      if (roundDiameterInput.value && roundHeightInput.value) {
        this.calculateRoundTank(uniqueId);
      }
    }

    if (rectangularLengthInput && rectangularWidthInput && rectangularHeightInput) {
      if (rectangularLengthInput.value && rectangularWidthInput.value && rectangularHeightInput.value) {
        this.calculateRectangularTank(uniqueId);
      }
    }
  },

  /**
   * Calculate total area for round tank
   * Formula: Bottom area (π * r²) + Wall area (π * d * h)
   * Where: r = radius, d = diameter, h = height
   * @param {string} uniqueId - Unique identifier for this calculator instance
   */
  calculateRoundTank(uniqueId) {
    const container = document.getElementById(`water-tank-calculator-${uniqueId}`);
    if (!container) return;

    const diameterInput = container.querySelector('#round-diameter-' + uniqueId);
    const heightInput = container.querySelector('#round-height-' + uniqueId);
    const resultElement = container.querySelector('#round-result-' + uniqueId);

    if (!diameterInput || !heightInput || !resultElement) return;

    const diameter = parseFloat(diameterInput.value);
    const height = parseFloat(heightInput.value);

    // Check if both values are valid
    if (isNaN(diameter) || isNaN(height) || diameter <= 0 || height <= 0) {
      this.updateResult(resultElement, null);
      return;
    }

    const radius = diameter / 2;
    const PI = Math.PI;

    // Bottom area = π * r²
    const bottomArea = PI * radius * radius;

    // Wall area = circumference * height = π * d * h
    const wallArea = PI * diameter * height;

    // Total area = bottom + walls
    const totalArea = bottomArea + wallArea;

    this.updateResult(resultElement, totalArea);
  },

  /**
   * Calculate total area for rectangular tank
   * Formula: Bottom area (l * w) + Wall area (2 * l * h + 2 * w * h)
   * Where: l = length, w = width, h = height
   * @param {string} uniqueId - Unique identifier for this calculator instance
   */
  calculateRectangularTank(uniqueId) {
    const container = document.getElementById(`water-tank-calculator-${uniqueId}`);
    if (!container) return;

    const lengthInput = container.querySelector('#rectangular-length-' + uniqueId);
    const widthInput = container.querySelector('#rectangular-width-' + uniqueId);
    const heightInput = container.querySelector('#rectangular-height-' + uniqueId);
    const resultElement = container.querySelector('#rectangular-result-' + uniqueId);

    if (!lengthInput || !widthInput || !heightInput || !resultElement) return;

    const length = parseFloat(lengthInput.value);
    const width = parseFloat(widthInput.value);
    const height = parseFloat(heightInput.value);

    // Check if all values are valid
    if (
      isNaN(length) ||
      isNaN(width) ||
      isNaN(height) ||
      length <= 0 ||
      width <= 0 ||
      height <= 0
    ) {
      this.updateResult(resultElement, null);
      return;
    }

    // Bottom area = length * width
    const bottomArea = length * width;

    // Wall area = 2 * (length * height) + 2 * (width * height)
    // This accounts for two opposite walls of each dimension
    const wallArea = 2 * length * height + 2 * width * height;

    // Total area = bottom + walls
    const totalArea = bottomArea + wallArea;

    this.updateResult(resultElement, totalArea);
  },

  /**
   * Update the result display
   * @param {HTMLElement} resultElement - The result container element
   * @param {number|null} area - The calculated area, or null if invalid
   */
  updateResult(resultElement, area) {
    if (!resultElement) return;

    const numberElement = resultElement.querySelector('.water-tank-calculator__result-number');
    if (!numberElement) return;

    if (area === null || isNaN(area)) {
      numberElement.textContent = '-';
      return;
    }

    // Format to 1 decimal place
    numberElement.textContent = area.toFixed(1);
  },
};

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    // Calculator will be initialized by the snippet's inline script
  });
} else {
  // DOM already loaded, but initialization will be handled by snippet
}

